import React, { useEffect, useRef } from 'react';
import { Animated, Easing, StyleSheet } from 'react-native';
import { BMPrimitives, useBMTheme } from '../../../theme';
import IcSystemCheckmark from '@traveloka/district-icon/IcSystemCheckmark';
import IcSymbolAddMinus from '@traveloka/district-icon/IcSymbolAddMinus';
const Checkmark = (props) => {
    const { value = false, hover, focus, disabled, error, indeterminate } = props;
    const checkmarkStyle = styles();
    const { BMComponents, BMPrimitives } = useBMTheme();
    // manage colors
    const getColor = () => {
        if (disabled) {
            return {
                backgroundColor: BMComponents.BMUiControlBgcolorRadioCheckboxDisabled,
                borderColor: BMComponents.BMStyleLineBordercolorBold,
                knobColor: BMComponents.BMUiControlBordercolorRadioKnobDisabled,
            };
        }
        else if (error) {
            return {
                backgroundColor: BMComponents.BMStyleSurfaceBgcolorError,
                borderColor: BMComponents.BMStyleLineBordercolorError,
                knobColor: BMComponents.BMStyleIconInkHighlightRed,
            };
        }
        else if (value) {
            return {
                backgroundColor: BMComponents.BMUiControlBgcolorRadioCheckboxSurfaceBgActive,
                borderColor: BMComponents.BMUiControlBordercolorRadioCheckboxLineOn,
                knobColor: BMComponents.BMUiControlBordercolorRadioKnobLine,
            };
        }
        else {
            return {
                backgroundColor: BMComponents.BMUiControlBgcolorRadioCheckboxSurfaceBg,
                borderColor: BMComponents.BMUiControlBordercolorRadioCheckboxLineOn,
                knobColor: BMComponents.BMUiControlBordercolorRadioKnobLine,
            };
        }
    };
    //animations
    const borderColorAnimation = useRef(new Animated.Value(0)).current;
    const knobColorAnimation = useRef(new Animated.Value(0)).current;
    const checkScaleAnimation = useRef(new Animated.Value(0)).current;
    const indeterminateScaleAnimation = useRef(new Animated.Value(0)).current;
    const borderColorInterpolation = borderColorAnimation.interpolate({
        // focused, normal, hovered, error
        inputRange: [-1, 0, 1],
        outputRange: [
            BMComponents.BMStyleLineBordercolorFocus,
            getColor().borderColor,
            BMComponents.BMUiControlBordercolorRadioCheckboxLineOff,
        ],
    });
    const checkScaleInterpolation = checkScaleAnimation.interpolate({
        inputRange: [0, 1],
        outputRange: [0, 1],
    });
    const indeterminateScaleInterpolation = indeterminateScaleAnimation.interpolate({
        inputRange: [0, 1],
        outputRange: [0, 1],
    });
    useEffect(() => {
        Animated.timing(borderColorAnimation, {
            toValue: hover ? 1 : 0,
            duration: BMPrimitives.BMAnimation.duration.instant,
            useNativeDriver: false,
        }).start();
        if (!hover) {
            Animated.parallel([
                Animated.timing(knobColorAnimation, {
                    toValue: 0,
                    duration: BMPrimitives.BMAnimation.duration.instant,
                    useNativeDriver: false,
                }),
            ]).start();
        }
    }, [hover]);
    useEffect(() => {
        Animated.parallel([
            Animated.timing(borderColorAnimation, {
                toValue: focus ? -1 : 0,
                duration: BMPrimitives.BMAnimation.duration.instant,
                useNativeDriver: false,
            }),
            Animated.timing(knobColorAnimation, {
                toValue: focus ? 1 : 0,
                duration: BMPrimitives.BMAnimation.duration.instant,
                useNativeDriver: false,
            }),
        ]).start();
    }, [focus]);
    useEffect(() => {
        const { easeOutBounce, easeInBounce } = BMPrimitives.BMAnimation.transition;
        Animated.parallel([
            Animated.timing(checkScaleAnimation, {
                toValue: value ? 1 : 0,
                duration: BMPrimitives.BMAnimation.duration.instant,
                useNativeDriver: false,
                easing: value
                    ? Easing.bezier(easeOutBounce.x1, easeOutBounce.y1, easeOutBounce.x2, easeOutBounce.y2)
                    : Easing.bezier(easeInBounce.x1, easeInBounce.y1, easeInBounce.x2, easeInBounce.y2),
            }),
            Animated.timing(indeterminateScaleAnimation, {
                toValue: indeterminate ? 1 : 0,
                duration: BMPrimitives.BMAnimation.duration.instant,
                useNativeDriver: false,
                easing: value
                    ? Easing.bezier(easeOutBounce.x1, easeOutBounce.y1, easeOutBounce.x2, easeOutBounce.y2)
                    : Easing.bezier(easeInBounce.x1, easeInBounce.y1, easeInBounce.x2, easeInBounce.y2),
            }),
        ]).start();
    }, [value, indeterminate, error, disabled]);
    return (<>
      <Animated.View style={[
            checkmarkStyle.checkmarkContainer,
            {
                backgroundColor: getColor()['backgroundColor'],
            },
        ]}>
        <Animated.View style={{
            position: 'absolute',
            transform: [{ scale: indeterminateScaleInterpolation }],
        }}>
          <IcSymbolAddMinus width={16} height={16} accentColor={getColor().knobColor}/>
        </Animated.View>
        <Animated.View style={{
            position: 'absolute',
            transform: [{ scale: checkScaleInterpolation }],
        }}>
          <IcSystemCheckmark width={16} height={16} accentColor={getColor().knobColor}/>
        </Animated.View>
      </Animated.View>
      {/* Outer Box */}
      <Animated.View style={[
            checkmarkStyle.outerRing,
            {
                borderColor: borderColorInterpolation,
                borderWidth: BMPrimitives.BMStroke.md,
            },
        ]}/>
    </>);
};
export default Checkmark;
const styles = () => {
    return StyleSheet.create({
        checkmarkContainer: {
            width: 16,
            height: 16,
            borderRadius: 2,
            position: 'relative',
            marginTop: 3,
            justifyContent: 'center',
            alignItems: 'center',
        },
        outerRing: {
            width: 22,
            height: 22,
            backgroundColor: 'transparent',
            position: 'absolute',
            borderRadius: BMPrimitives.BMBorder.radius.default,
            left: -3,
        },
    });
};
